import { buildRankedTokens, getSuggestedTokens, itemMatchesActiveTokens, itemMatchesQuery } from './token-search.js';
import { escapeHtml } from './utils.js';

const PICKER_STYLES = `
:host { display: contents; }

.lp-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 1200;
}

.lp-dialog {
    position: relative;
    top: 40px;
    width: min(720px, calc(100% - 36px));
    max-height: calc(100% - 80px);
    background: var(--lp-bg, #fff);
    border-radius: 10px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.22);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.lp-header {
    display: flex;
    align-items: center;
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--lp-border, #e8e8e8);
    gap: 12px;
}

.lp-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--lp-text, #222);
    flex: 1;
}

.lp-close {
    border: none;
    background: none;
    font-size: 18px;
    cursor: pointer;
    color: var(--lp-text-secondary, #888);
    padding: 2px 6px;
    line-height: 1;
}

.lp-body {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    padding: 12px 16px 0;
}

.lp-tabs {
    display: flex;
    gap: 2px;
    margin-bottom: 10px;
    border-bottom: 1px solid var(--lp-border, #e8e8e8);
}

.lp-tab {
    border: none;
    background: none;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    color: var(--lp-text-secondary, #6b7a8a);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: color 0.15s, border-color 0.15s;
}

.lp-tab:hover { color: var(--lp-text, #333); }

.lp-tab.active {
    color: var(--lp-accent, #2a6cb6);
    border-bottom-color: var(--lp-accent, #2a6cb6);
    font-weight: 600;
}

.lp-search-row {
    display: flex;
    gap: 8px;
    align-items: center;
}

.lp-search {
    flex: 1;
    padding: 8px 10px;
    font-size: 13px;
    border: 1px solid var(--lp-border, #ccc);
    border-radius: 6px;
    outline: none;
    background: var(--lp-bg, #fff);
    color: var(--lp-text, #222);
}

.lp-search:focus {
    border-color: var(--lp-accent, #2a6cb6);
    box-shadow: 0 0 0 2px rgba(42, 108, 182, 0.12);
}

.lp-search-clear {
    border: none;
    background: none;
    font-size: 11px;
    color: var(--lp-accent, #2a6cb6);
    cursor: pointer;
    padding: 4px 6px;
    font-weight: 600;
}

.lp-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
}

.lp-pills:empty { display: none; }

.lp-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--lp-pill-border, #bfd4ea);
    background: var(--lp-pill-bg, #dfeeff);
    color: var(--lp-pill-text, #17456a);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    padding: 6px 10px;
    cursor: pointer;
}

.lp-pill:hover { background: var(--lp-pill-hover, #cde4fb); }
.lp-pill-x { font-size: 12px; font-weight: 700; }

.lp-items {
    flex: 1;
    overflow-y: auto;
    margin-top: 4px;
    padding-bottom: 8px;
}

.lp-suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 0;
}

.lp-suggestions:empty { display: none; }

.lp-suggestion {
    border: 1px solid var(--lp-pill-border, #bfd4ea);
    background: var(--lp-suggestion-bg, #eef6ff);
    color: var(--lp-pill-text, #17456a);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    padding: 6px 10px;
    cursor: pointer;
}

.lp-suggestion:hover { background: var(--lp-pill-hover, #e0eefc); }

.lp-item {
    padding: 8px 10px;
    cursor: pointer;
    border-bottom: 1px solid var(--lp-border-light, #f0f0f0);
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.lp-item:hover { background: var(--lp-hover, #f0f6ff); }

.lp-item-content { flex: 1; min-width: 0; }

.lp-item-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--lp-text, #222);
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.lp-item-title-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
}

.lp-item-subtitle {
    font-size: 11px;
    color: var(--lp-text-secondary, #888);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.lp-item-meta {
    font-size: 10px;
    color: var(--lp-text-secondary, #999);
    margin-top: 2px;
}

.lp-badge {
    display: inline-block;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 2px 6px;
    border-radius: 3px;
    background: var(--lp-badge-bg, #eef2f7);
    color: var(--lp-badge-text, #556);
    flex-shrink: 0;
}

.lp-more {
    padding: 6px 10px;
    font-size: 11px;
    color: var(--lp-text-secondary, #aaa);
    font-style: italic;
}

.lp-empty {
    padding: 20px 10px;
    font-size: 12px;
    color: var(--lp-text-secondary, #999);
    text-align: center;
}
`;

const MAX_VISIBLE = 60;

/**
 * Generic list picker with token-pill search and tabbed item sources.
 *
 * Usage:
 *   const picker = document.createElement('hs-list-picker');
 *   hostShadowRoot.appendChild(picker);
 *   picker.open({
 *       title: 'Choose Topic',
 *       tabs: [
 *           { id: 'sources', label: 'Data Sources', items: [...],
 *             getSearchText: s => [s.path, s.name, s.description],
 *             renderItem: s => ({ title: s.name, subtitle: s.description, badges: ['rollup'] }),
 *             getValue: s => s.name },
 *       ],
 *       onSelect: (value, item, tabId) => { ... },
 *   });
 */
export class HSListPicker extends HTMLElement {
    #config = null;
    #activeTab = '';
    #tokens = new Map();        // tabId → ranked token array
    #activeTokens = [];
    #root = null;

    constructor() {
        super();
        this.#root = this.attachShadow({ mode: 'open' });
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(PICKER_STYLES);
        this.#root.adoptedStyleSheets = [sheet];
    }

    /**
     * Open the picker modal.
     * @param {Object} config
     * @param {string} config.title
     * @param {Array} config.tabs - [{ id, label, items, getSearchText, renderItem, getValue, minTokenLength, emptyText }]
     * @param {Function} config.onSelect - (value, item, tabId) => void
     * @param {Function} [config.onClose]
     */
    open(config) {
        this.#config = config;
        this.#activeTokens = [];

        // Build ranked tokens for each tab
        this.#tokens.clear();
        for (const tab of config.tabs) {
            this.#tokens.set(tab.id, buildRankedTokens(tab.items, {
                getSearchText: tab.getSearchText,
                minTokenLength: tab.minTokenLength ?? 3,
            }));
        }

        this.#activeTab = config.tabs[0]?.id || '';
        this.#render();
        this.#bindEvents();

        const input = this.#root.querySelector('.lp-search');
        if (input) requestAnimationFrame(() => input.focus());
    }

    close() {
        this.#root.innerHTML = '';
        if (this.#config?.onClose) this.#config.onClose();
        this.#config = null;
    }

    /** Update items for a specific tab (e.g. after async fetch). */
    updateTabItems(tabId, items) {
        const tab = this.#config?.tabs?.find(t => t.id === tabId);
        if (!tab) return;
        tab.items = items;
        this.#tokens.set(tabId, buildRankedTokens(items, {
            getSearchText: tab.getSearchText,
            minTokenLength: tab.minTokenLength ?? 3,
        }));
        if (this.#activeTab === tabId) {
            this.#renderItems(this.#root.querySelector('.lp-search')?.value || '');
        }
    }

    #render() {
        const cfg = this.#config;
        const showTabs = cfg.tabs.length > 1;

        let tabsHtml = '';
        if (showTabs) {
            tabsHtml = `<div class="lp-tabs">${cfg.tabs.map(t =>
                `<button class="lp-tab${t.id === this.#activeTab ? ' active' : ''}" data-tab="${escapeHtml(t.id)}">${escapeHtml(t.label)}</button>`
            ).join('')}</div>`;
        }

        this.#root.innerHTML = `
            <div class="lp-overlay">
                <div class="lp-dialog" role="dialog" aria-modal="true" aria-label="${escapeHtml(cfg.title)}">
                    <div class="lp-header">
                        <div class="lp-title">${escapeHtml(cfg.title)}</div>
                        <button class="lp-close" title="Close">&#x2715;</button>
                    </div>
                    <div class="lp-body">
                        ${tabsHtml}
                        <div class="lp-search-row">
                            <input type="text" class="lp-search" placeholder="Type to search..." autocomplete="off" spellcheck="false" />
                            <button class="lp-search-clear">Clear</button>
                        </div>
                        <div class="lp-pills"></div>
                        <div class="lp-items">
                            <div class="lp-suggestions"></div>
                        </div>
                    </div>
                </div>
            </div>`;

        this.#renderItems('');
    }

    #bindEvents() {
        const root = this.#root;
        const overlay = root.querySelector('.lp-overlay');
        const input = root.querySelector('.lp-search');

        overlay.addEventListener('click', (ev) => { if (ev.target === overlay) this.close(); });
        root.querySelector('.lp-close').addEventListener('click', () => this.close());
        root.querySelector('.lp-search-clear').addEventListener('mousedown', (ev) => {
            ev.preventDefault();
            this.#activeTokens = [];
            input.value = '';
            this.#renderPills();
            this.#renderItems('');
            input.focus();
        });

        input.addEventListener('input', () => {
            if (input.value === '') {
                this.#activeTokens = [];
                this.#renderPills();
            }
            this.#renderItems(input.value);
        });

        // Tab switching
        for (const tab of root.querySelectorAll('.lp-tab')) {
            tab.addEventListener('click', () => {
                this.#activeTab = tab.dataset.tab;
                root.querySelectorAll('.lp-tab').forEach(t => t.classList.toggle('active', t === tab));
                this.#activeTokens = [];
                input.value = '';
                this.#renderPills();
                this.#renderItems('');
                input.focus();
            });
        }

        // Keyboard: Escape to close
        input.addEventListener('keydown', (ev) => {
            if (ev.key === 'Escape') this.close();
        });
    }

    #getActiveTab() {
        return this.#config?.tabs?.find(t => t.id === this.#activeTab) || null;
    }

    #renderPills() {
        const container = this.#root.querySelector('.lp-pills');
        if (!container) return;
        container.innerHTML = this.#activeTokens.map(token =>
            `<button class="lp-pill"><span>${escapeHtml(token)}</span><span class="lp-pill-x">×</span></button>`
        ).join('');

        for (const pill of container.querySelectorAll('.lp-pill')) {
            pill.addEventListener('click', () => {
                const token = pill.querySelector('span').textContent;
                this.#activeTokens = this.#activeTokens.filter(t => t !== token);
                this.#renderPills();
                const input = this.#root.querySelector('.lp-search');
                this.#renderItems(input?.value || '');
            });
        }
    }

    #renderItems(query) {
        const tab = this.#getActiveTab();
        if (!tab) return;

        // Suggestions
        const sugContainer = this.#root.querySelector('.lp-suggestions');
        if (sugContainer) {
            const ranked = this.#tokens.get(tab.id) || [];
            const suggestions = getSuggestedTokens(ranked, this.#activeTokens, query, {
                minQueryLength: 2,
                maxSuggestions: 6,
            });
            sugContainer.innerHTML = suggestions.map(t =>
                `<button class="lp-suggestion">${escapeHtml(t)}</button>`
            ).join('');

            for (const btn of sugContainer.querySelectorAll('.lp-suggestion')) {
                btn.addEventListener('mousedown', (ev) => {
                    ev.preventDefault();
                    this.#addToken(btn.textContent);
                });
            }
        }

        // Filter items
        const items = (tab.items || []).filter(item => {
            if (!itemMatchesActiveTokens(item, this.#activeTokens, tab.getSearchText)) return false;
            return itemMatchesQuery(item, query, tab.getSearchText);
        });

        const listContainer = this.#root.querySelector('.lp-items');
        // Remove everything except .lp-suggestions
        Array.from(listContainer.children).forEach(child => {
            if (!child.classList.contains('lp-suggestions')) child.remove();
        });

        const shown = items.slice(0, MAX_VISIBLE);
        for (const item of shown) {
            const rendered = tab.renderItem(item);
            const el = document.createElement('div');
            el.className = 'lp-item';

            const badgesHtml = (rendered.badges || [])
                .filter(Boolean)
                .map(b => `<span class="lp-badge">${escapeHtml(b)}</span>`)
                .join('');

            el.innerHTML = `<div class="lp-item-content">
                <div class="lp-item-title"><span class="lp-item-title-text">${escapeHtml(rendered.title)}</span>${badgesHtml}</div>
                ${rendered.subtitle ? `<div class="lp-item-subtitle">${escapeHtml(rendered.subtitle)}</div>` : ''}
                ${rendered.meta ? `<div class="lp-item-meta">${escapeHtml(rendered.meta)}</div>` : ''}
            </div>`;

            el.addEventListener('mousedown', (ev) => {
                ev.preventDefault();
                const value = tab.getValue ? tab.getValue(item) : item;
                const onSelect = this.#config?.onSelect;
                this.close();
                if (onSelect) onSelect(value, item, tab.id);
            });

            listContainer.appendChild(el);
        }

        if (items.length > MAX_VISIBLE) {
            const more = document.createElement('div');
            more.className = 'lp-more';
            more.textContent = `…${items.length - MAX_VISIBLE} more — type to filter`;
            listContainer.appendChild(more);
        }

        if (items.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'lp-empty';
            empty.textContent = (this.#activeTokens.length > 0 || query)
                ? 'No items match the search'
                : (tab.emptyText || 'No items available');
            listContainer.appendChild(empty);
        }
    }

    #addToken(token) {
        const normalized = String(token || '').trim().toLowerCase();
        if (!normalized || this.#activeTokens.includes(normalized)) return;
        this.#activeTokens = [...this.#activeTokens, normalized];
        const input = this.#root.querySelector('.lp-search');
        if (input) { input.value = ''; input.focus(); }
        this.#renderPills();
        this.#renderItems('');
    }
}

customElements.define('hs-list-picker', HSListPicker);
